// Per-item row components for the structured-mode intake-form UI
// (Slice 2). Used by the fill modal (write) and review modal (read +
// elect-toggle) so the structured-mode visual pattern stays
// consistent across both surfaces.
//
// Map-builder helpers live here too: the fill modal seeds
// tier_1_responses / tier_2_responses by reading the form's saved
// jsonb and coercing each item-id slot into the strict in-memory
// shape; the review modal does the same for tier_2_responses.

import {
  coerceTier1Response,
  coerceTier2Response,
  emptyTier1Response,
  emptyTier2Response,
  tier1ChoiceFrom,
  tier1ResponseFromChoice,
  type Tier1Response,
  type Tier2Response,
} from "@/lib/fulfillment/intakeResponseShape";
import type { IntakeTemplateItem } from "@/lib/fulfillment/templateItem";

export function buildTier1Map(
  items: IntakeTemplateItem[],
  raw: Record<string, unknown>
): Record<string, Tier1Response> {
  const map: Record<string, Tier1Response> = {};
  for (const it of items) {
    map[it.id] = it.id in raw ? coerceTier1Response(raw[it.id]) : emptyTier1Response();
  }
  return map;
}

export function buildTier2Map(
  items: IntakeTemplateItem[],
  raw: Record<string, unknown>
): Record<string, Tier2Response> {
  const map: Record<string, Tier2Response> = {};
  for (const it of items) {
    map[it.id] = it.id in raw ? coerceTier2Response(raw[it.id]) : emptyTier2Response();
  }
  return map;
}

// ---------------------------------------------------------------------------
// Fill modal (write)
// ---------------------------------------------------------------------------

export function FillTier1Section({
  items,
  responses,
  onChange,
}: {
  items: IntakeTemplateItem[];
  responses: Record<string, Tier1Response>;
  onChange: (itemId: string, patch: Partial<Tier1Response>) => void;
}) {
  return (
    <fieldset className="rounded-md border border-gray-200 p-3 dark:border-zinc-700">
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-zinc-200">
        Tier 1 — Mandatory inspection
      </legend>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Template has no Tier-1 items; nothing to inspect.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const r = responses[it.id] ?? emptyTier1Response();
            const choice = tier1ChoiceFrom(r);
            return (
              <li key={it.id} className="rounded-md bg-gray-50 p-2 dark:bg-zinc-800">
                <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{it.label}</div>
                <div className="mt-1 flex gap-3 text-sm">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`t1-${it.id}`}
                      checked={choice === "pass"}
                      onChange={() => onChange(it.id, tier1ResponseFromChoice("pass", "", r.notes))}
                    />
                    <span>Pass</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`t1-${it.id}`}
                      checked={choice === "na"}
                      onChange={() =>
                        onChange(it.id, tier1ResponseFromChoice("na", r.n_a_reason, r.notes))
                      }
                    />
                    <span>N/A</span>
                  </label>
                </div>
                {choice === "na" ? (
                  <input
                    type="text"
                    value={r.n_a_reason}
                    onChange={(e) => onChange(it.id, { n_a_reason: e.target.value })}
                    placeholder="Why N/A?"
                    className="mt-2 w-full rounded-md border border-gray-300 bg-white p-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                  />
                ) : null}
                <input
                  type="text"
                  value={r.notes}
                  onChange={(e) => onChange(it.id, { notes: e.target.value })}
                  placeholder="Notes (optional)"
                  className="mt-2 w-full rounded-md border border-gray-300 bg-white p-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                />
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}

export function FillTier2Section({
  items,
  responses,
  onChange,
}: {
  items: IntakeTemplateItem[];
  responses: Record<string, Tier2Response>;
  onChange: (itemId: string, patch: Partial<Tier2Response>) => void;
}) {
  return (
    <fieldset className="rounded-md border border-gray-200 p-3 dark:border-zinc-700">
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-zinc-200">
        Tier 2 — Optional upgrades
      </legend>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Template has no Tier-2 items; nothing to suggest.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const r = responses[it.id] ?? emptyTier2Response();
            return (
              <li key={it.id} className="rounded-md bg-gray-50 p-2 dark:bg-zinc-800">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={r.elected}
                    onChange={(e) => onChange(it.id, { elected: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span className="font-medium text-gray-900 dark:text-zinc-100">{it.label}</span>
                </label>
                <input
                  type="text"
                  value={r.notes}
                  onChange={(e) => onChange(it.id, { notes: e.target.value })}
                  placeholder="Notes (optional)"
                  className="mt-2 w-full rounded-md border border-gray-300 bg-white p-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                />
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Review modal (read + elect-toggle)
// ---------------------------------------------------------------------------

export function ReviewTier1Section({
  items,
  responses,
}: {
  items: IntakeTemplateItem[];
  responses: Record<string, unknown>;
}) {
  return (
    <fieldset className="rounded-md border border-gray-200 p-3 dark:border-zinc-700">
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-zinc-200">
        Tier 1 — Inspection responses
      </legend>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Template has no Tier-1 items.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const r = coerceTier1Response(responses[it.id]);
            const choice = tier1ChoiceFrom(r);
            const choiceLabel =
              choice === "pass"
                ? "Pass"
                : choice === "na"
                ? `N/A — ${r.n_a_reason || "no reason given"}`
                : "Unanswered";
            const choiceColor =
              choice === "pass"
                ? "text-green-700 dark:text-green-400"
                : choice === "na"
                ? "text-amber-700 dark:text-amber-400"
                : "text-red-700 dark:text-red-400";
            return (
              <li key={it.id} className="rounded-md bg-gray-50 p-2 dark:bg-zinc-800">
                <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{it.label}</div>
                <div className={`text-xs ${choiceColor}`}>{choiceLabel}</div>
                {r.notes ? (
                  <div className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
                    Notes: {r.notes}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}

export function ReviewTier2Section({
  items,
  responses,
  onToggle,
}: {
  items: IntakeTemplateItem[];
  responses: Record<string, Tier2Response>;
  onToggle: (itemId: string, elected: boolean) => void;
}) {
  return (
    <fieldset className="rounded-md border border-gray-200 p-3 dark:border-zinc-700">
      <legend className="px-1 text-sm font-medium text-gray-700 dark:text-zinc-200">
        Tier 2 — Elect for work
      </legend>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Template has no Tier-2 items.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const r = responses[it.id] ?? emptyTier2Response();
            return (
              <li key={it.id} className="rounded-md bg-gray-50 p-2 dark:bg-zinc-800">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={r.elected}
                    onChange={(e) => onToggle(it.id, e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="font-medium text-gray-900 dark:text-zinc-100">{it.label}</span>
                </label>
                {r.notes ? (
                  <div className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
                    Lot worker notes: {r.notes}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}
